import { useFeatureFlag } from '@agentnet/feature-flags';
import { EmptyState } from '@fluentsms/agentnet-web-components';
import { DoneEmptyState, ErrorEmptyState, StopEmptyState } from '@fluentsms/agentnet-web-icons';
import { Box, Paper } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { ErrorOutline } from '@material-ui/icons';
import {
  getTitleMonitoringConfig,
  getTitleMonitoringEnrollment,
  getTitleMonitoringInviteByFirmIdAndPropertyDetailId,
  getTitleMonitoringInviteByInvitationGuid,
  titleMonitoringSignup,
  verifyTitleMonitoringPropertyAddress,
} from 'api/titleMonitoring/titleMonitoring-api';
import { TitleMonitoringConfig, TitleMonitoringInvite } from 'api/titleMonitoring/types';
import { AGENCY_SUPPORT_EMAIL } from 'features/admin/constants';
import { RenderContent } from 'features/help/components/RenderContent/RenderContent';
import { useMessagingQuery } from 'features/help/queries/useMessagingQuery';
import { Message } from 'features/help/types/messagingCollection';
import DomesticAddressField from 'features/files/file-create/DomesticAddress';
import { useAnonymousTracking } from 'hooks/AnonymousTrackingContext';
import { ChangeEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { Redirect, useLocation, useParams } from 'react-router-dom';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import { AddressFields } from 'ui-kit/form/interfaces/AddressModal';
import { ANFooterLogo } from 'ui-kit/icons/Logo';
import { AgentNetTextInput } from 'ui-kit/inputs';
import PhoneField from 'ui-kit/inputs/PhoneField/PhoneField';
import { FeatureFlag } from 'utilities/constants/launch-darkly-flags';
import {
  titleMonitoringPropertyAddressSchema,
  titleMonitoringSignupSchema,
} from 'utilities/validation/schemas/title-monitoring-signup-schema';
import { doValidate, FieldValidationError } from 'utilities/validation/validation';
import {
  TITLE_MONITORING_PROPERTY_NOT_ELIGIBLE_TITLE,
  TITLE_MONITORING_PROPERTY_NOT_VERIFIED_TITLE,
  trackTitleMonitoringSignupFailure,
} from 'utilities/fullstory/fullStoryEvents';
import TitleMonitoringFooter from './TitleMonitoringFooter';

const formatInvitePropertyAddress = (invite: TitleMonitoringInvite): string =>
  [invite.address1Line, invite.address2Line, `${invite.cityName}, ${invite.stateCode} ${invite.zipCode}`]
    .filter((addressPart) => addressPart?.trim())
    .join(', ');

const formatAddressFields = (address: AddressFields): string =>
  [
    address.address1,
    address.address2,
    `${address.city ?? ''}, ${(address.stateOrProvince ?? address.state ?? '').toString()} ${
      address.postalCode ?? ''
    }`.trim(),
  ]
    .filter((addressPart) => addressPart?.trim())
    .join(', ');

const TITLE_MONITORING_EULA_SLUG = 'first-american-property-monitoring-end-user-license-agreement-eula';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    page: {
      alignItems: 'center',
      background: '#EBF4FA',
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      padding: theme.spacing(4, 2),
      width: '100%',
    },
    paper: {
      borderRadius: 6,
      marginBottom: theme.spacing(3),
      maxWidth: 720,
      overflow: 'hidden',
      width: '100%',
    },
    logoContainer: {
      alignItems: 'center',
      display: 'flex',
      height: 60,
      justifyContent: 'center',
      marginBottom: theme.spacing(3),
      '& svg': {
        transform: 'scale(1.5)',
      },
    },
    agentLogo: {
      maxHeight: 85,
      maxWidth: 300,
      objectFit: 'contain',
    },
    heading: {
      marginBottom: theme.spacing(0.5),
    },
    subtext: {
      marginBottom: theme.spacing(2.5),
    },
    eulaSubtext: {
      marginBottom: theme.spacing(2),
    },
    eulaBox: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 4,
      height: 300,
      marginBottom: theme.spacing(3),
      overflowY: 'scroll',
      padding: theme.spacing(2),
      '& .kbArticleBody': {
        color: theme.palette.text.secondary,
      },
      '& .kbArticleBody h1, & .kbArticleBody h2, & .kbArticleBody h3, & .kbArticleBody h4, & .kbArticleBody h5, & .kbArticleBody h6':
        {
          color: theme.palette.text.secondary,
        },
      '& .kbArticleBody a': {
        color: theme.palette.primary.main,
        textDecoration: 'underline',
        textDecorationColor: theme.palette.primary.main,
        '&:hover': {
          color: theme.palette.primary.dark,
        },
        '&:visited': {
          color: theme.palette.primary.main,
        },
      },
    },
    actions: {
      display: 'flex',
      gap: theme.spacing(1.5),
    },
    addressFields: {
      /* DomesticAddressField hardcodes xs sizes, so the fields are stacked here for small screens */
      [theme.breakpoints.down('xs')]: {
        '& .MuiGrid-item': {
          flexBasis: '100%',
          maxWidth: '100%',
        },
      },
    },
    addressActions: {
      marginTop: theme.spacing(2),
    },
    summaryBlock: {
      marginBottom: theme.spacing(2.5),
    },
    summaryLabel: {
      fontWeight: 700,
    },
    emailLink: {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
      '&:hover': {
        color: theme.palette.primary.dark,
      },
    },
    policyErrorBox: {
      alignItems: 'flex-start',
      backgroundColor: '#F6E3E2',
      border: '1px solid #E8B9B6',
      borderRadius: 4,
      display: 'flex',
      marginTop: theme.spacing(2),
      padding: theme.spacing(1.5, 2),
      width: '100%',
    },
    policyErrorIcon: {
      color: '#AC1209',
      flexShrink: 0,
      fontSize: theme.typography.pxToRem(22),
      marginRight: theme.spacing(1.5),
      marginTop: theme.spacing(0.125),
    },
    policyErrorText: {
      alignSelf: 'stretch',
      flex: 1,
      minWidth: 0,
    },
    policyErrorTitle: {
      color: '#5F2120',
      fontFamily: 'Inter, sans-serif',
      fontSize: theme.typography.pxToRem(14),
      fontStyle: 'normal',
      fontWeight: 500,
      letterSpacing: '0.15px',
      lineHeight: '150%',
      marginBottom: theme.spacing(0.5),
    },
    policyErrorMessage: {
      color: '#5F2120',
      fontFamily: 'Inter, sans-serif',
      fontSize: theme.typography.pxToRem(12),
      fontStyle: 'normal',
      fontWeight: 400,
      letterSpacing: '0.15px',
      lineHeight: '143%',
      '& a': {
        color: 'inherit',
        fontWeight: 'inherit',
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
    emptyStateRoot: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      textAlign: 'center',
      width: '100%',
    },
    emptyStateIconContainer: {
      marginBottom: theme.spacing(2),
      '& svg': {
        height: 120,
        width: 250,
      },
    },
    emptyStateHeader: {
      marginBottom: theme.spacing(1),
    },
    emptyStateDescription: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(2),
      maxWidth: 640,
    },
    loadingContainer: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
      minHeight: 240,
      width: '100%',
    },
  }),
);

type SignupFormState = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

type TitleMonitoringView =
  | 'property-address-input'
  | 'signup'
  | 'eula'
  | 'eula-declined'
  | 'enrollment-success'
  | 'enrollment-existed'
  | 'property-ineligible'
  | 'signup-failed';

type TitleMonitoringNotificationError = {
  title: string;
  message: ReactNode;
};

type PendingFailureEvent = {
  errorTitle: string;
  propertyAddress: string;
};

/* Copy that depends on the firm config is resolved at render time, since the config loads after some errors are set */
type TitleMonitoringNotificationErrorTemplate =
  | TitleMonitoringNotificationError
  | { title: string; getMessage: (agentName?: string) => ReactNode };

const resolveNotificationError = (
  error: TitleMonitoringNotificationErrorTemplate,
  agentName?: string,
): TitleMonitoringNotificationError =>
  'getMessage' in error ? { title: error.title, message: error.getMessage(agentName) } : error;

const SIGNUP_SUBMIT_NOTIFICATION_ERROR: TitleMonitoringNotificationError = {
  title: 'Unable to Sign Up for Title Monitoring Alerts',
  message: 'Unable to sign up for alerts. Please try again.',
};

const ADDRESS_VALIDATION_NOTIFICATION_ERROR: TitleMonitoringNotificationError = {
  title: 'Unable to Validate Address',
  message: 'Unable to validate the address. Please try again.',
};

const PROPERTY_NOT_ELIGIBLE_NOTIFICATION_ERROR: TitleMonitoringNotificationError = {
  title: TITLE_MONITORING_PROPERTY_NOT_ELIGIBLE_TITLE,
  message: 'Property is located in a state or county that is not eligible for monitoring.',
};

const PROPERTY_NOT_VERIFIED_NOTIFICATION_ERROR: TitleMonitoringNotificationErrorTemplate = {
  title: TITLE_MONITORING_PROPERTY_NOT_VERIFIED_TITLE,
  getMessage: (agentName?: string) =>
    `We're unable to confirm this property in our system. Please check the address and try again, or contact ${
      agentName?.trim() || 'your title company'
    } for assistance.`,
};

const EULA_CONTENT_NOTIFICATION_ERROR: TitleMonitoringNotificationError = {
  title: 'Unable to Load End User License Agreement',
  message: 'Unable to load the End User License Agreement. Please try again.',
};

type TitleMonitoringErrorNotificationProps = {
  error: TitleMonitoringNotificationError;
  classes: ReturnType<typeof useStyles>;
};

const TitleMonitoringErrorNotification = ({ error, classes }: TitleMonitoringErrorNotificationProps): JSX.Element => (
  <Box className={classes.policyErrorBox} role="alert">
    <ErrorOutline className={classes.policyErrorIcon} />
    <Box className={classes.policyErrorText}>
      <Typography className={classes.policyErrorTitle}>{error.title}</Typography>
      <Typography className={classes.policyErrorMessage} component="div">
        {error.message}
      </Typography>
    </Box>
  </Box>
);

const TitleMonitoringSignup = (): JSX.Element | null => {
  const classes = useStyles();
  const { setFirmName } = useAnonymousTracking();
  const location = useLocation();
  const { firmGuid } = useParams<{ firmGuid?: string }>();
  const inviteGuid = new URLSearchParams(location.search).get('invite');
  const { enabled: isTitleMonitoringEnabled, ready: isTitleMonitoringFlagReady } = useFeatureFlag(
    FeatureFlag.TITLE_MONITORING_ENABLED,
    false,
  );
  const [hasEvaluatedTitleMonitoringFlag, setHasEvaluatedTitleMonitoringFlag] = useState(false);
  const [view, setView] = useState<TitleMonitoringView>(
    inviteGuid ? 'signup' : firmGuid ? 'property-address-input' : 'signup-failed',
  );
  const [signupForm, setSignupForm] = useState<SignupFormState>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [addressInput, setAddressInput] = useState<AddressFields>({} as AddressFields);
  const [isAddressSubmitted, setIsAddressSubmitted] = useState<boolean>(false);
  const [isValidatingAddress, setIsValidatingAddress] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);
  const [notificationError, setNotificationError] = useState<TitleMonitoringNotificationErrorTemplate | null>(null);
  const [pendingFailureEvent, setPendingFailureEvent] = useState<PendingFailureEvent | null>(null);
  const [invite, setInvite] = useState<TitleMonitoringInvite | null>(null);
  const [config, setConfig] = useState<TitleMonitoringConfig | null>(null);
  const [hasLogoError, setHasLogoError] = useState<boolean>(false);
  const [isLoadingInvite, setIsLoadingInvite] = useState<boolean>(!!inviteGuid);
  const [isLoadingConfig, setIsLoadingConfig] = useState<boolean>(!!firmGuid || !!inviteGuid);
  const [isCheckingEnrollment, setIsCheckingEnrollment] = useState<boolean>(false);
  const [eulaContent, setEulaContent] = useState<Message | null>(null);
  const [isLoadingEula, setIsLoadingEula] = useState<boolean>(false);
  const [hasEulaError, setHasEulaError] = useState<boolean>(false);

  const inviteFirmId = invite?.firmId;
  const agentLogoUrl = !hasLogoError ? config?.agentLogoUrl : null;
  const allowedStates = useMemo(
    () =>
      config?.agentAvailableStates
        ?.split(',')
        .map((stateCode) => stateCode.trim())
        .filter(Boolean) ?? [],
    [config?.agentAvailableStates],
  );
  const propertyAddress = invite ? formatInvitePropertyAddress(invite) : '';
  const resolvedNotificationError = useMemo(
    () => (notificationError ? resolveNotificationError(notificationError, config?.agentName) : null),
    [notificationError, config?.agentName],
  );

  const clearNotificationError = () => setNotificationError(null);

  /* The invite flow fails before the firm config resolves, so the event is queued and emitted once agentName is known */
  const showNotificationError = (error: TitleMonitoringNotificationErrorTemplate, propertyAddress?: string) => {
    setNotificationError(error);

    if (propertyAddress) {
      setPendingFailureEvent({ errorTitle: error.title, propertyAddress });
    }
  };

  const handleValidateAddress = async () => {
    setIsAddressSubmitted(true);
    clearNotificationError();

    const addressErrors = (await doValidate(addressInput, titleMonitoringPropertyAddressSchema)) || [];
    if (addressErrors.length > 0) {
      return;
    }

    /* Without a firm there is nothing to look the property up against, same as a failed config load */
    if (!config?.firmId) {
      handleSignupFailed();
      return;
    }

    setIsValidatingAddress(true);
    const verifiedProperties = await verifyTitleMonitoringPropertyAddress(addressInput);

    if (!verifiedProperties) {
      showNotificationError(ADDRESS_VALIDATION_NOTIFICATION_ERROR);
      setIsValidatingAddress(false);
      return;
    }

    const propertyDetailId = verifiedProperties.length === 1 ? verifiedProperties[0].propertyDetailId : undefined;

    if (!propertyDetailId) {
      showNotificationError(PROPERTY_NOT_VERIFIED_NOTIFICATION_ERROR, formatAddressFields(addressInput));
      setIsValidatingAddress(false);
      return;
    }

    const matchedInvite = await getTitleMonitoringInviteByFirmIdAndPropertyDetailId(config.firmId, propertyDetailId);
    setIsValidatingAddress(false);

    if (!matchedInvite) {
      showNotificationError(PROPERTY_NOT_VERIFIED_NOTIFICATION_ERROR, formatAddressFields(addressInput));
      return;
    }

    if (matchedInvite.isCountyExcluded) {
      showNotificationError(PROPERTY_NOT_ELIGIBLE_NOTIFICATION_ERROR, formatAddressFields(addressInput));
      return;
    }

    setInvite(matchedInvite);
    setView('signup');
  };

  const handleSignup = async () => {
    setIsSubmitted(true);
    if (validationErrors?.length > 0 || !invite?.propertyDetailId) {
      return;
    }

    setIsCheckingEnrollment(true);
    clearNotificationError();
    const enrollment = await getTitleMonitoringEnrollment(invite.propertyDetailId, signupForm.email);
    setIsCheckingEnrollment(false);

    if (enrollment) {
      setView('enrollment-existed');
      return;
    }

    setView('eula');
  };

  const handleDecline = () => {
    setView('eula-declined');
  };

  const handleReviewEula = () => {
    setView('eula');
  };

  const handleSignupFailed = () => {
    setView('signup-failed');
  };

  /* Terminal state for the invite link flow, where there is no address field for the user to correct */
  const handlePropertyIneligible = (error: TitleMonitoringNotificationErrorTemplate, propertyAddress?: string) => {
    showNotificationError(error, propertyAddress);
    setView('property-ineligible');
  };

  const handleAccept = async () => {
    if (!invite) {
      return;
    }

    clearNotificationError();
    setIsSending(true);
    const requestObj = {
      titleMonitoringInviteId: invite.titleMonitoringInviteId,
      propertyId: invite.propertyId,
      firstName: signupForm.firstName,
      lastName: signupForm.lastName,
      email: signupForm.email,
      phone: signupForm.phone,
    };
    if (await titleMonitoringSignup(requestObj)) {
      setView('enrollment-success');
    } else {
      showNotificationError(SIGNUP_SUBMIT_NOTIFICATION_ERROR);
    }
    setIsSending(false);
  };

  useEffect(() => {
    doValidate(signupForm, titleMonitoringSignupSchema()).then((errs: FieldValidationError[]) => {
      setValidationErrors(errs);
    });
  }, [signupForm]);

  useEffect(() => {
    if (isTitleMonitoringFlagReady) {
      setHasEvaluatedTitleMonitoringFlag(true);
    }
  }, [isTitleMonitoringFlagReady]);

  useEffect(() => {
    if (!inviteGuid) {
      return;
    }

    setIsLoadingInvite(true);
    getTitleMonitoringInviteByInvitationGuid(inviteGuid).then((result) => {
      if (result) {
        setInvite(result);
        if (!result.firmId) {
          setIsLoadingConfig(false);
        }
        /* The property address can change after the invite is sent, so eligibility is re-checked on load */
        if (!result.propertyDetailId) {
          handlePropertyIneligible(PROPERTY_NOT_VERIFIED_NOTIFICATION_ERROR, formatInvitePropertyAddress(result));
        } else if (result.isCountyExcluded) {
          handlePropertyIneligible(PROPERTY_NOT_ELIGIBLE_NOTIFICATION_ERROR, formatInvitePropertyAddress(result));
        }
      } else {
        handleSignupFailed();
        setIsLoadingConfig(false);
      }
      setIsLoadingInvite(false);
    });
  }, [inviteGuid]);

  useEffect(() => {
    const trimmedAgentName = config?.agentName?.trim();
    setFirmName(trimmedAgentName || undefined);

    return () => {
      setFirmName(undefined);
    };
  }, [config?.agentName, setFirmName]);

  useEffect(() => {
    if (!pendingFailureEvent || isLoadingConfig) {
      return;
    }

    trackTitleMonitoringSignupFailure(
      pendingFailureEvent.errorTitle,
      pendingFailureEvent.propertyAddress,
      config?.agentName,
    );
    setPendingFailureEvent(null);
  }, [pendingFailureEvent, isLoadingConfig, config?.agentName]);

  useEffect(() => {
    if (!firmGuid && !inviteFirmId) {
      return;
    }

    setIsLoadingConfig(true);
    setHasLogoError(false);
    getTitleMonitoringConfig(firmGuid ? { firmGuid } : { firmId: inviteFirmId }).then((result) => {
      if (result) {
        setConfig(result);
      } else {
        handleSignupFailed();
      }
      setIsLoadingConfig(false);
    });
  }, [firmGuid, inviteFirmId]);

  useEffect(() => {
    if (allowedStates.length !== 1) {
      return;
    }

    const singleState = allowedStates[0].toUpperCase();
    setAddressInput((prev) => {
      const currentState = (prev.stateOrProvince ?? prev.state ?? '').toString().toUpperCase();
      if (currentState === singleState) {
        return prev;
      }
      return { ...prev, stateOrProvince: singleState };
    });
  }, [allowedStates]);

  useEffect(() => {
    if (view !== 'eula' || eulaContent || isLoadingEula) {
      return;
    }

    setIsLoadingEula(true);
    setHasEulaError(false);
    useMessagingQuery(TITLE_MONITORING_EULA_SLUG).then((result) => {
      if (result.error || !result.data?.messageText?.json) {
        setHasEulaError(true);
      } else {
        setEulaContent(result.data);
      }
      setIsLoadingEula(false);
    });
  }, [view, eulaContent, isLoadingEula]);

  if (!isTitleMonitoringFlagReady || !hasEvaluatedTitleMonitoringFlag) {
    return null;
  }

  if (!isTitleMonitoringEnabled) {
    return <Redirect to="/" />;
  }

  return (
    <div className={classes.page}>
      <Paper className={classes.paper} elevation={2}>
        <Box p={4}>
          {isLoadingInvite || isLoadingConfig ? (
            <Box className={classes.loadingContainer}>
              <LoadingSpinner status="pending" className="files-container--spinner-root" />
            </Box>
          ) : (
            <>
              <div className={classes.logoContainer}>
                {agentLogoUrl ? (
                  <img
                    src={agentLogoUrl}
                    alt={config?.agentName ? `${config.agentName} logo` : 'Company logo'}
                    className={classes.agentLogo}
                    onError={() => setHasLogoError(true)}
                    data-testid="titleMonitoringHeaderLogo"
                  />
                ) : (
                  <ANFooterLogo />
                )}
              </div>
              {view === 'property-address-input' ? (
                <>
                  <Typography variant="h3" className={classes.heading}>
                    Sign Up for Title Monitoring Alerts
                  </Typography>
                  <Typography variant="body1" className={classes.subtext}>
                    Enter your property address to get started.
                  </Typography>
                  <div className={classes.addressFields}>
                    <DomesticAddressField
                      value={addressInput}
                      onChange={setAddressInput}
                      useGoogleAutocomplete
                      getAllState
                      showCounty={false}
                      displayRequiredAddressFields
                      schema={titleMonitoringPropertyAddressSchema}
                      showAllValidation={isAddressSubmitted}
                      allowedStates={allowedStates}
                      disableState={allowedStates.length === 1}
                      cityGridSize={6}
                      qaAttributePrefix="titleMonitoring"
                    />
                  </div>
                  <Grid container spacing={2} className={classes.addressActions}>
                    <Grid item xs={12}>
                      <AgentNetButton
                        color="primary"
                        size="small"
                        className="button"
                        variant="contained"
                        onClick={handleValidateAddress}
                        loading={isValidatingAddress}
                        disabled={isValidatingAddress}
                        data-testid="titleMonitoringValidateAddress"
                      >
                        Validate Address
                      </AgentNetButton>
                    </Grid>
                    {resolvedNotificationError && (
                      <Grid item xs={12}>
                        <TitleMonitoringErrorNotification error={resolvedNotificationError} classes={classes} />
                      </Grid>
                    )}
                  </Grid>
                </>
              ) : view === 'signup' ? (
                <>
                  <Typography variant="h3" className={classes.heading}>
                    Sign Up for Title Monitoring Alerts
                  </Typography>
                  <Typography variant="body1" className={classes.subtext}>
                    Enter your contact information to receive Title Monitoring alerts for your property.
                  </Typography>
                  {propertyAddress && (
                    <Box className={classes.summaryBlock}>
                      <Typography variant="body1">
                        <span className={classes.summaryLabel}>Address:</span> {propertyAddress}
                      </Typography>
                    </Box>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <AgentNetTextInput
                        fullWidth
                        variant="outlined"
                        label="First Name"
                        name="firstName"
                        id="titleMonitoringFirstName"
                        required
                        data-testid="titleMonitoringFirstName"
                        value={signupForm.firstName}
                        showValidation={isSubmitted}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          setSignupForm({ ...signupForm, firstName: event.target.value })
                        }
                        errs={validationErrors}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <AgentNetTextInput
                        fullWidth
                        variant="outlined"
                        label="Last Name"
                        name="lastName"
                        id="titleMonitoringLastName"
                        required
                        data-testid="titleMonitoringLastName"
                        value={signupForm.lastName}
                        showValidation={isSubmitted}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          setSignupForm({ ...signupForm, lastName: event.target.value })
                        }
                        errs={validationErrors}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <AgentNetTextInput
                        fullWidth
                        variant="outlined"
                        label="Email"
                        name="email"
                        id="titleMonitoringEmail"
                        required
                        data-testid="titleMonitoringEmail"
                        value={signupForm.email}
                        showValidation={isSubmitted}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          setSignupForm({ ...signupForm, email: event.target.value })
                        }
                        errs={validationErrors}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <PhoneField
                        fullWidth
                        variant="outlined"
                        label="Phone"
                        name="phone"
                        id="titleMonitoringPhone"
                        data-testid="titleMonitoringPhone"
                        value={signupForm.phone}
                        showValidation={isSubmitted}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          setSignupForm({ ...signupForm, phone: event.target.value })
                        }
                        errs={validationErrors}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <AgentNetButton
                        color="primary"
                        size="small"
                        className="button"
                        variant="contained"
                        onClick={handleSignup}
                        loading={isCheckingEnrollment}
                        disabled={isCheckingEnrollment}
                        data-testid="titleMonitoringSignUp"
                      >
                        Sign Up for Alerts
                      </AgentNetButton>
                    </Grid>
                  </Grid>
                </>
              ) : view === 'eula' ? (
                <>
                  <Typography variant="h3" className={classes.heading}>
                    Title Monitoring End User License Agreement
                  </Typography>
                  <Typography variant="body1" className={classes.eulaSubtext}>
                    Review and accept the EULA to receive Title Monitoring alerts for your property.
                  </Typography>
                  {isLoadingEula ? (
                    <Box className={classes.eulaBox}>
                      <Box className={classes.loadingContainer}>
                        <LoadingSpinner status="pending" className="files-container--spinner-root" />
                      </Box>
                    </Box>
                  ) : hasEulaError || !eulaContent ? (
                    <Box mb={3}>
                      <TitleMonitoringErrorNotification error={EULA_CONTENT_NOTIFICATION_ERROR} classes={classes} />
                    </Box>
                  ) : (
                    <Box className={classes.eulaBox}>
                      <RenderContent document={eulaContent.messageText} color="textSecondary" />
                    </Box>
                  )}
                  <div className={classes.actions}>
                    <AgentNetButton
                      color="primary"
                      size="small"
                      variant="contained"
                      onClick={handleAccept}
                      loading={isSending}
                      disabled={isSending || isLoadingEula || !eulaContent}
                      data-testid="titleMonitoringEulaAccept"
                    >
                      Accept
                    </AgentNetButton>
                    <AgentNetButton
                      color="primary"
                      size="small"
                      variant="outlined"
                      onClick={handleDecline}
                      disabled={isSending}
                      data-testid="titleMonitoringEulaDecline"
                    >
                      Decline
                    </AgentNetButton>
                  </div>
                  <div>
                    {resolvedNotificationError && (
                      <TitleMonitoringErrorNotification error={resolvedNotificationError} classes={classes} />
                    )}
                  </div>
                </>
              ) : view === 'eula-declined' ? (
                <Box py={2}>
                  <EmptyState
                    icon={<StopEmptyState style={{ fontSize: 80 }} />}
                    headerText="EULA Declined"
                    descriptionText="You must accept the End User License Agreement (EULA) in order to receive Title Monitoring alerts. If you declined by mistake, you can review it again below."
                    primaryButtonProps={{
                      label: 'Review EULA',
                      onClick: handleReviewEula,
                      variant: 'contained',
                      color: 'primary',
                    }}
                  />
                </Box>
              ) : view === 'property-ineligible' ? (
                <Box py={2}>
                  <Box className={classes.emptyStateRoot} data-testid="titleMonitoringPropertyIneligible">
                    <Box className={classes.emptyStateIconContainer}>
                      <StopEmptyState />
                    </Box>
                    <Typography variant="h4" component="h2" className={classes.emptyStateHeader}>
                      {resolvedNotificationError?.title}
                    </Typography>
                    <Typography variant="body1" className={classes.emptyStateDescription} component="div">
                      {resolvedNotificationError?.message}
                    </Typography>
                  </Box>
                </Box>
              ) : view === 'signup-failed' ? (
                <Box py={2}>
                  <Box className={classes.emptyStateRoot}>
                    <Box className={classes.emptyStateIconContainer}>
                      <ErrorEmptyState />
                    </Box>
                    <Typography variant="h4" component="h2" className={classes.emptyStateHeader}>
                      Unable to Sign Up for Title Monitoring Alerts
                    </Typography>
                    <Typography variant="body1" className={classes.emptyStateDescription}>
                      The invitation code could not be verified. Please contact the Agency Service Center at{' '}
                      <a href={`mailto:${AGENCY_SUPPORT_EMAIL}`} className={classes.emailLink}>
                        {AGENCY_SUPPORT_EMAIL}
                      </a>{' '}
                      or (866) 701-3361 for assistance.
                    </Typography>
                  </Box>
                </Box>
              ) : view === 'enrollment-existed' ? (
                <Box py={2}>
                  <EmptyState
                    icon={<DoneEmptyState style={{ fontSize: 80 }} />}
                    headerText="You're already enrolled in Title Monitoring."
                    descriptionText="Your enrollment has already been received and is being processed. Your dashboard will be available within 24 hours. We'll send a confirmation email when it's ready."
                  />
                </Box>
              ) : (
                <Box py={2}>
                  <EmptyState
                    icon={<DoneEmptyState style={{ fontSize: 80 }} />}
                    headerText="Enrollment Successful"
                    descriptionText="Title Monitoring has been activated for your property. A confirmation email will be sent within 24 hours to the email address provided."
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Paper>
      <TitleMonitoringFooter />
    </div>
  );
};

export default TitleMonitoringSignup;
